import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, TextField, InputAdornment, IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Edit, Search, Add } from '@mui/icons-material';

import AddEmployees from './AddEmployees';
import EditEmployees from './EditEmployees';
import StatusBadge from '../../../Components/Badges/StatusBadge';
import axiosInstance from '../../../Config/axiosInstance';
import './Employees.css';

const STATUS_VARIANTS = {
  Pending: 'pending',
  pending: 'pending',
  Completed: 'success',
  completed: 'success',
  Critical: 'critical',
  critical: 'critical',
  Active: 'success',
  active: 'success',
  activ: 'success',
  Expirat: 'critical',
  expirat: 'critical',
  Inactive: 'critical',
  inactive: 'critical',
  inactiv: 'critical',
  expired: 'critical',
  Suspended: 'pending',
  suspended: 'pending',
};

const SEARCH_DEBOUNCE_MS = 300;

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
};

const useEmployees = () => {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEmployers = useCallback(async () => {
    try {
      setLoading(true);

      const empRes = await axiosInstance.get('/employees/');
      const employeesData = empRes.data;

      const updated = await Promise.all(
        employeesData.map(async (emp) => {
          let ssmStatus = '-';
          let medStatus = '-';

          try {
            const [ssmRes, medRes] = await Promise.allSettled([
              axiosInstance.get(`/employees/${emp.id}/ssm/`),
              axiosInstance.get(`/employees/${emp.id}/medical/`),
            ]);

            if (
              ssmRes.status === 'fulfilled' &&
              Array.isArray(ssmRes.value.data.ssm_records)
            ) {
              const records = ssmRes.value.data.ssm_records;
              if (records.length > 0) {
                const last = records[records.length - 1];
                ssmStatus = last.status || last.result || '-';
              }
            }

            if (
              medRes.status === 'fulfilled' &&
              Array.isArray(medRes.value.data.medical_checkups)
            ) {
              const records = medRes.value.data.medical_checkups;
              if (records.length > 0) {
                const last = records[records.length - 1];
                medStatus = last.status || '-';
              }
            }
          } catch (e) {
            console.warn(`status error ${emp.first_name}:`, e);
          }

          return {
            ...emp,
            department_name: emp.department_label || '-',
            type_name: emp.type_label || '-',
            ssm_status: ssmStatus,
            medicina_muncii_status: medStatus,
          };
        })
      );

      const sorted = updated.sort((a, b) => {
        const sa = a.status?.trim().toLowerCase() || '';
        const sb = b.status?.trim().toLowerCase() || '';

        if (sa === 'active' && sb !== 'active') return -1;
        if (sa !== 'active' && sb === 'active') return 1;

        if (sa === 'suspended' && sb === 'inactive') return -1;
        if (sa === 'inactive' && sb === 'suspended') return 1;

        return 0;
      });

      setEmployers(sorted);
    } catch (err) {
      console.error('fetchEmployers error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { employers, loading, fetchEmployers };
};

const Employees = () => {
  const navigate = useNavigate();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canView, setCanView] = useState(false);

  useEffect(() => {
    const requiredPath = "/employees";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const checkPermission = (key) =>
      permissions.some(page => {
        try {
          const pagePath = new URL(page.url).pathname;
          return pagePath === requiredPath && page[key] === true;
        } catch {
          return false;
        }
      });

    setCanCreate(checkPermission('can_create'));
    setCanEdit(checkPermission('can_edit'));
    setCanView(checkPermission('can_view'));
  }, []);

  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const { employers, loading, fetchEmployers } = useEmployees();

  useEffect(() => {
    fetchEmployers();
  }, [fetchEmployers]);

  const getStatusVariant = useCallback(
    (status) => STATUS_VARIANTS[status?.trim()] || 'default',
    []
  );

  const handleViewDetails = useCallback(
    (row) => {
      if (canView && row?.id) navigate(`/employees/${row.id}/medical`);
    },
    [navigate, canView]
  );

  const handleEditEmployee = useCallback((employee) => {
    if (!canEdit) return;

    setSelectedEmployee({
      ...employee,
    });

    setOpenEditModal(true);
  }, [canEdit]);

  const filteredRows = useMemo(() => {
    let list = Array.isArray(employers) ? [...employers] : [];

    if (debouncedSearchTerm) {
      const s = debouncedSearchTerm.toLowerCase();

      list = list.filter((e) => {
        const first = e?.first_name?.toLowerCase() || '';
        const last = e?.last_name?.toLowerCase() || '';
        const full = `${first} ${last}`;

        return (
          first.includes(s) ||
          last.includes(s) ||
          full.includes(s) ||
          e?.code?.toLowerCase().includes(s) ||
          e?.department_label?.toLowerCase().includes(s) ||
          e?.type_label?.toLowerCase().includes(s)
        );
      });
    }

    return list.map((e, idx) => ({
      id: e?.id ?? idx,
      code: e?.code ?? `EMP-${idx}`,
      ...e,
    }));
  }, [employers, debouncedSearchTerm]);

  const columns = useMemo(() => {
    const baseColumns = [
      { field: 'code', headerName: 'Code', flex: 0.8, minWidth: 110 },
      {
        field: 'full_name',
        headerName: 'Employee name',
        flex: 1.4,
        minWidth: 180,
        renderCell: (params) =>
          `${params.row.first_name || ''} ${params.row.last_name || ''}`,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 130,
        renderCell: (params) => {
          const status = params?.row?.status || '-';
          const formatted =
            status && status !== '-'
              ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
              : '-';

          return (
            <StatusBadge variant={getStatusVariant(status)} text={formatted} />
          );
        },
      },
      {
        field: 'department_label',
        headerName: 'Department',
        flex: 1.1,
        minWidth: 150,
        renderCell: (params) => (
          <span>{params.value && params.value !== '-' ? params.value : '–'}</span>
        ),
      },
      {
        field: 'contract_date',
        headerName: 'Start date',
        flex: 0.9,
        minWidth: 140,
        renderCell: (params) => {
          const date = params?.row?.contract_date;
          const formattedDate = date
            ? new Date(date).toLocaleDateString("ro-RO")
            : "-";
          return <span>{formattedDate}</span>;
        },
      },
      {
        field: 'type_label',
        headerName: 'Contract Type',
        flex: 0.9,
        minWidth: 140,
        renderCell: (params) => (
          <span>{params.value && params.value !== '-' ? params.value : '–'}</span>
        ),
      },
      {
        field: 'ssm_status',
        headerName: 'SSM',
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => {
          const value = params?.value?.trim();
          if (!value || value === '-' || value === 'null') {
            return <span style={{ color: '#777', fontStyle: 'italic' }}>–</span>;
          }

          const formatted =
            value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

          return (
            <StatusBadge variant={getStatusVariant(value)} text={formatted} />
          );
        },
      },
      {
        field: 'medicina_muncii_status',
        headerName: 'Medicina',
        flex: 0.9,
        minWidth: 130,
        renderCell: (params) => {
          const value = params?.value?.trim();
          if (!value || value === '-' || value === 'null') {
            return <span style={{ color: '#777', fontStyle: 'italic' }}>–</span>;
          }

          const formatted =
            value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

          return (
            <StatusBadge variant={getStatusVariant(value)} text={formatted} />
          );
        },
      },
    ];

    if (canView || canEdit) {
      baseColumns.push({
        field: 'action',
        headerName: 'Action',
        width: 120,
        renderCell: (params) => (
          <div style={{ display: 'flex', gap: '8px' }}>
            {canView && (
              <IconButton
                onClick={() => handleViewDetails(params.row)}
                sx={{ color: '#093d71ff' }}
              >
                <Visibility />
              </IconButton>
            )}
            {canEdit && (
              <IconButton
                onClick={() => handleEditEmployee(params.row)}
                sx={{ color: '#1976d2' }}
              >
                <Edit />
              </IconButton>
            )}
          </div>
        ),
        sortable: false,
        disableColumnMenu: true,
      });
    }

    return baseColumns;
  }, [canView, canEdit, getStatusVariant, handleViewDetails, handleEditEmployee]);

  return (
    <div className="employeespage">
      {showToast && <div className="employees-toast">{toastMessage}</div>}

      <div className="employees-page">
        <Box className="employees-toolbar">
          <h2 className="title">
            EMPLOYEES
            <span className="title-count">
              {' '}
              ({filteredRows.length}
              {filteredRows.length !== (employers?.length ?? 0) &&
                ` of ${employers?.length ?? 0}`})
            </span>
          </h2>

          <Box className="employees-toolbar-right">
            <TextField
              size="small"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#424242' }} />
                  </InputAdornment>
                ),
              }}
            />

            {canCreate && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenAddModal(true)}
                className="new-btn"
              >
                New
              </Button>
            )}
          </Box>
        </Box>

        <div className="table-container">
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            getRowHeight={() => "auto"}
            pageSize={5}
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
              pagination: {
                paginationModel: { pageSize: 12 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.4",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                whiteSpace: "normal",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .Mui-selected:hover": {
                backgroundColor: "transparent !important",
              },
            }}
            pageSizeOptions={[10, 12, 20, 25]}
          />
        </div>

        <AddEmployees
          open={openAddModal}
          onClose={(shouldReload) => {
            setOpenAddModal(false);
            if (shouldReload) {
              fetchEmployers();
              setToastMessage("✅ Employee added successfully!");
              setShowToast(true);
              setTimeout(() => setShowToast(false), 4000);
            }
          }}
        />

        <EditEmployees
          open={openEditModal}
          employeeData={selectedEmployee}
          onClose={(shouldReload, message) => {
            setOpenEditModal(false);
            setSelectedEmployee(null);
            if (shouldReload) {
              fetchEmployers();
              if (message) {
                setToastMessage(message);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 4000);
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default Employees;